/**
 * Dahua IP video intercom catalog — VTH indoor monitors + VTO outdoor
 * stations.
 *
 * All specs verified against dahuasecurity.com product pages on
 * 2026-05-15. URL cited per entry. Missing specs left undefined.
 *
 * `type` mapping: standard IP VTH/VTO use 'ip-proprietary' (Dahua's
 * DSS protocol, not standard SIP); 2-wire bus VTH5422 series use
 * 'audio-video-2wire'.
 *
 * accessControl mapping: VTO source values translated to schema enum:
 *   - 'card' → 'mifare' (Dahua VTO IC card reader is Mifare-class)
 *   - 'fingerprint' → 'fingerprint'
 *   - 'face' / 'password' → dropped (not in schema enum)
 *
 * VTO9541D-S3 has a 10.1" touchscreen on the outdoor station for face
 * recognition — `monitorInches` omitted on outdoor entries to avoid
 * picker confusion (the field is typed as indoor-monitor-relevant).
 */

import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const DAHUA_INTERCOM_CATALOG: EquipmentCatalogEntry[] = [
  // ─────────────────────────────────────────────────────────────
  // Indoor monitors — VTH series
  // ─────────────────────────────────────────────────────────────

  {
    id: 'dahua-vth5341g-w',
    manufacturer: 'Dahua',
    model: 'VTH5341G-W',
    category: 'intercom',
    tagline: '10-inch Android IP and Wi-Fi indoor monitor',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/products/All-Products/Video-Intercoms/IP-Products/Indoor-Monitors/VTH5341G-W',
    specs: { type: 'ip-proprietary', side: 'indoor-monitor', monitorInches: 10, poe: true },
  },

  {
    id: 'dahua-vth2421fb-p',
    manufacturer: 'Dahua',
    model: 'VTH2421FB-P',
    category: 'intercom',
    tagline: '7-inch IP indoor monitor with PoE (black)',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/mena/products/All-Products/Video-Intercoms/IP-Series/Indoor-Monitor/Pro-Series/VTH2421FB-P/VTH2421FW-P',
    specs: { type: 'ip-proprietary', side: 'indoor-monitor', monitorInches: 7, poe: true },
  },

  {
    id: 'dahua-vth2421fw-p',
    manufacturer: 'Dahua',
    model: 'VTH2421FW-P',
    category: 'intercom',
    tagline: '7-inch IP indoor monitor with PoE (white)',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/mena/products/All-Products/Video-Intercoms/IP-Series/Indoor-Monitor/Pro-Series/VTH2421FB-P/VTH2421FW-P',
    specs: { type: 'ip-proprietary', side: 'indoor-monitor', monitorInches: 7, poe: true },
  },

  {
    id: 'dahua-vth5422hb-w',
    manufacturer: 'Dahua',
    model: 'VTH5422HB-W',
    category: 'intercom',
    tagline: '7-inch 2-wire Wi-Fi indoor monitor (black)',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/mena/products/All-Products/Video-Intercoms/2-Wire-IP/Indoor-Monitor/Pro-Series/VTH5422HB-W/VTH5422HW-W',
    specs: { type: 'audio-video-2wire', side: 'indoor-monitor', monitorInches: 7, poe: false },
  },

  {
    id: 'dahua-vth5422hw-w',
    manufacturer: 'Dahua',
    model: 'VTH5422HW-W',
    category: 'intercom',
    tagline: '7-inch 2-wire Wi-Fi indoor monitor (white)',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/mena/products/All-Products/Video-Intercoms/2-Wire-IP/Indoor-Monitor/Pro-Series/VTH5422HB-W/VTH5422HW-W',
    specs: { type: 'audio-video-2wire', side: 'indoor-monitor', monitorInches: 7, poe: false },
  },

  {
    id: 'dahua-vth2611l-wp',
    manufacturer: 'Dahua',
    model: 'VTH2611L-WP',
    category: 'intercom',
    tagline: '4.3-inch IP and Wi-Fi indoor monitor',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/products/all-products/video-intercoms/ip-series/indoor-monitor/lite-series/vth2611l-wp',
    specs: { type: 'ip-proprietary', side: 'indoor-monitor', monitorInches: 4.3, poe: true },
  },

  {
    id: 'dahua-vth8641kms-wp',
    manufacturer: 'Dahua',
    model: 'VTH8641KMS-WP',
    category: 'intercom',
    tagline: '10-inch Ultra-series IP and Wi-Fi indoor monitor',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/products/All-Products/Video-Intercoms/IP-Series/Indoor-Monitor/Ultra-Series/VTH8641KMS-WP',
    specs: { type: 'ip-proprietary', side: 'indoor-monitor', monitorInches: 10, poe: true },
  },

  // ─────────────────────────────────────────────────────────────
  // Outdoor stations — VTO series
  // ─────────────────────────────────────────────────────────────

  {
    id: 'dahua-vto2202f-p-s3',
    manufacturer: 'Dahua',
    model: 'VTO2202F-P-S3',
    category: 'intercom',
    tagline: '2 MP 1-button villa door station, PoE and 2-wire support',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/products/All-Products/Video-Intercoms/2-Wire-IP/Villa-Door-Station/Pro-Series/VTO2202F-P-S3',
    specs: {
      type: 'ip-proprietary',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 1,
      ipRating: 'IP65',
      poe: true,
    },
  },

  {
    id: 'dahua-vto9541d-s3',
    manufacturer: 'Dahua',
    model: 'VTO9541D-S3',
    category: 'intercom',
    tagline: '10.1-inch IP face-recognition apartment door station with multi-factor auth',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/products/All-Products/Video-Intercoms/IP-Series/Apartment-Door-Station/Ultra-Series/VTO9541D-S3',
    specs: {
      type: 'ip-proprietary',
      side: 'outdoor-station',
      cameraMP: 2,
      ipRating: 'IP65',
      poe: true,
      accessControl: ['fingerprint', 'mifare', 'keypad'],
    },
  },

  {
    id: 'dahua-vto2111d-p-s3',
    manufacturer: 'Dahua',
    model: 'VTO2111D-P-S3',
    category: 'intercom',
    tagline: '2 MP 1-button villa door station with IC card unlock',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/mena/products/All-Products/Video-Intercoms/IP-Series/Villa-Door-Station/Lite-Series/VTO2111D-P-S3',
    specs: {
      type: 'ip-proprietary',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 1,
      ipRating: 'IP65',
      poe: true,
      accessControl: ['mifare'],
    },
  },

  {
    id: 'dahua-vto2311r-wp',
    manufacturer: 'Dahua',
    model: 'VTO2311R-WP',
    category: 'intercom',
    tagline: '2 MP IP and Wi-Fi villa door station with card unlock',
    verified: true,
    datasheetUrl: 'https://www.dahuasecurity.com/products/All-Products/Video-Intercoms/IP-Series/Villa-Door-Station/Pro-Series/VTO2311R-WP',
    specs: {
      type: 'ip-proprietary',
      side: 'outdoor-station',
      cameraMP: 2,
      ipRating: 'IP65',
      poe: true,
      accessControl: ['mifare'],
    },
  },

  {
    id: 'dahua-vto2202f-p-s2',
    manufacturer: 'Dahua',
    model: 'VTO2202F-P-S2',
    category: 'intercom',
    tagline: '2 MP 1-button villa door station, PoE and 2-wire support',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/VTO2202F-P-S2_datasheet_20230105.pdf',
    specs: {
      type: 'ip-proprietary',
      side: 'outdoor-station',
      cameraMP: 2,
      callButtons: 1,
      poe: true,
    },
  },

  {
    id: 'dahua-vto4202f-p-s2',
    manufacturer: 'Dahua',
    model: 'VTO4202F-P-S2',
    category: 'intercom',
    tagline: '2 MP modular apartment door station, PoE and 2-wire support',
    verified: true,
    datasheetUrl: 'https://material.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/VTO4202F-P-S2_datasheet_20221028.pdf',
    specs: {
      type: 'ip-proprietary',
      side: 'outdoor-station',
      cameraMP: 2,
      poe: true,
    },
  },
];
